/**
 * The permission keys this code gates on. The API's registry is the source of
 * the list and of how each permission is named; these are the keys the
 * dashboard has call sites for, so a key the API adds and this code does not
 * yet know is left out of what it draws rather than rendered ungated.
 */
export const PERMISSION_KEYS = [
  "orders.create",
  "orders.cancel",
  "orders.decide_requests",
  "catalog.manage",
  "catalog.activate_products",
  "campaigns.manage",
] as const;

export type PermissionKey = typeof PERMISSION_KEYS[number];

export function isPermissionKey(value: unknown): value is PermissionKey {
  return (
    typeof value === "string" &&
    (PERMISSION_KEYS as readonly string[]).includes(value)
  );
}

/**
 * One permission as the API describes it: its group is the part of the
 * dashboard it unlocks, and the label and description are what the Team page
 * shows an admin deciding what to grant. The order of the registry is the
 * order they are shown in.
 */
export interface PermissionRegistryEntry {
  key: PermissionKey;
  group: string;
  label: string;
  description: string;
}

/**
 * Reads the registry off a profile payload, keeping only entries this code
 * knows the key of and that carry the text the editor needs.
 */
export function toPermissionRegistry(data: unknown): PermissionRegistryEntry[] {
  if (!Array.isArray(data)) return [];
  return data.filter(isPermissionRegistryEntry);
}

function isPermissionRegistryEntry(
  value: unknown
): value is PermissionRegistryEntry {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    isPermissionKey(entry.key) &&
    typeof entry.group === "string" &&
    typeof entry.label === "string" &&
    typeof entry.description === "string"
  );
}

/**
 * The codes the API answers with when it refuses a dashboard user for what
 * their profile says they may do: a missing permission, or a partner-wide page
 * closed to a user who sees only some locations.
 */
const ACCESS_REFUSAL_CODES: readonly unknown[] = [
  "permission_required",
  "all_locations_required",
];

/**
 * Whether a failed response is the API refusing an action for something the
 * profile records — as opposed to "not yours" (401) or "admin only" (403
 * without a code). Either way the profile the screen was drawn from is stale.
 */
export function isPermissionRefusal(status: number, body: unknown): boolean {
  return (
    status === 403 &&
    typeof body === "object" &&
    body !== null &&
    ACCESS_REFUSAL_CODES.includes((body as { code?: unknown }).code)
  );
}
